import { IMediaAssetModel, VIDEO_QUALITY_FALLBACK_HEIGHT, VIDEO_RESOLUTION_OPTIONS } from "../../Services/MediaAssetService";
import { VideoPlayerSource } from "./VideoPlayer";

const TRANSCODED_MIME_TYPE = "video/mp4";

const canPlayInBrowser = (mimeType?: string): boolean =>
    !!mimeType && document.createElement("video").canPlayType(mimeType) !== "";

/**
 * The player's sources, straight from the media list — every URL in it is already signed, so pressing play
 * costs no request. The renditions are the ones that exist: the backend never upscales, and there are none
 * while the video is still processing. The uploaded file itself is only offered when it adds something: as
 * the top quality when it is taller than the best rendition, or as the only source while there is no
 * rendition yet — and in both cases only when this browser can play its format at all.
 */
export const resolveVideoPlayerSources = (asset: IMediaAssetModel): VideoPlayerSource[] => {
    const sources: VideoPlayerSource[] = [];
    (asset.renditions ?? []).forEach((rendition) => {
        const option = VIDEO_RESOLUTION_OPTIONS.find((candidate) => candidate.variantType === rendition.variantType);
        if (option) {
            sources.push({ src: rendition.url, size: option.height, type: TRANSCODED_MIME_TYPE });
        }
    });
    if (!asset.originalUrl || !canPlayInBrowser(asset.mimeType)) {
        return sources;
    }
    const bestRenditionHeight = Math.max(0, ...sources.map((source) => source.size));
    if (sources.length === 0) {
        sources.push({ src: asset.originalUrl, size: asset.sourceHeight ?? VIDEO_QUALITY_FALLBACK_HEIGHT, type: asset.mimeType });
    } else if (asset.sourceHeight !== undefined && asset.sourceHeight > bestRenditionHeight) {
        sources.push({ src: asset.originalUrl, size: asset.sourceHeight, type: asset.mimeType });
    }
    return sources;
};
